import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MealPlanDto, MealSlot } from '@fewerbags/schema';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { formatDayLong } from '../../lib/planDates';
import { SLOT_LABEL, SLOT_OPTIONS } from '../../lib/plans';
import { recipeKeys } from '../../pages/recipes/queries';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { SelectField } from '../SelectField';
import { RecipePicker } from './RecipePicker';

/**
 * "Add meal" from an empty slot on the week grid: pick a recipe, confirm
 * servings and the serve time, and the plan is created in one step.
 */
export function AddMealDialog({
  open,
  onClose,
  date,
  slot: initialSlot,
  defaultServings,
  defaultServeAt = '',
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  date: string;
  slot: MealSlot;
  defaultServings: number;
  defaultServeAt?: string;
  onCreated?: (plan: MealPlanDto) => void;
}) {
  const qc = useQueryClient();
  const [servings, setServings] = useState(defaultServings);
  const [serveAt, setServeAt] = useState(defaultServeAt);
  const [slot, setSlot] = useState<MealSlot>(initialSlot);
  const [picked, setPicked] = useState<{ id: string; title: string } | null>(null);

  // Re-open on another slot → start from the household defaults again.
  useEffect(() => {
    if (!open) return;
    setServings(defaultServings);
    setServeAt(defaultServeAt);
    setSlot(initialSlot);
    setPicked(null);
  }, [open, date, initialSlot, defaultServings, defaultServeAt]);

  const create = useMutation({
    mutationFn: (recipeId: string | null) =>
      api.plans.create({
        date,
        slot,
        servings,
        ...(serveAt ? { serveAt } : {}),
        recipes: recipeId ? [{ recipeId }] : [],
      }),
    onError: () => setPicked(null),
    onSuccess: async (plan) => {
      await qc.invalidateQueries({ queryKey: queryKeys.plans.all });
      // A shared recipe is forked into this household when planned (fork-on-use).
      void qc.invalidateQueries({ queryKey: recipeKeys.all });
      onCreated?.(plan);
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} title={`Add ${SLOT_LABEL[initialSlot].toLowerCase()}`}>
      <p className="text-sm text-muted">{formatDayLong(date)}</p>

      <div className="flex flex-wrap gap-3">
        <div className="min-w-32 flex-1">
          <SelectField
            id="add-meal-slot"
            label="Meal"
            value={slot}
            onChange={(e) => setSlot(e.target.value as MealSlot)}
            options={SLOT_OPTIONS}
          />
        </div>
        <div className="flex min-w-32 flex-1 flex-col gap-1">
          <label htmlFor="add-meal-servings" className="text-sm font-medium text-ink">
            Servings
          </label>
          <input
            id="add-meal-servings"
            type="number"
            min={1}
            max={50}
            value={servings}
            onChange={(e) => {
              const n = Number(e.target.value);
              setServings(Number.isFinite(n) && n > 0 ? Math.min(50, Math.round(n)) : 1);
            }}
            className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
          />
        </div>
        <div className="flex min-w-32 flex-1 flex-col gap-1">
          <label htmlFor="add-meal-serve-at" className="text-sm font-medium text-ink">
            Serve at
          </label>
          <input
            id="add-meal-serve-at"
            type="time"
            value={serveAt}
            onChange={(e) => setServeAt(e.target.value)}
            className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
          />
        </div>
      </div>

      {picked ? (
        <p className="rounded-lg bg-brand-soft px-3 py-2 text-sm text-brand">
          Adding <strong>{picked.title}</strong>…
        </p>
      ) : (
        <RecipePicker
          busy={create.isPending}
          onPick={(recipe) => {
            setPicked({ id: recipe.id, title: recipe.title });
            create.mutate(recipe.id);
          }}
        />
      )}

      {create.isError ? <Alert tone="error">{messageForError(create.error)}</Alert> : null}

      <div className="flex gap-2">
        <Button
          variant="secondary"
          disabled={create.isPending}
          onClick={() => {
            setPicked(null);
            create.mutate(null);
          }}
        >
          Save an empty meal
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
}
